import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const ShoppingContext = createContext();

// Función para extraer el mensaje de error
const getErrorMessage = (error) => {
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  return error.message || "Error desconocido";
};

export const ShoppingProvider = ({ children }) => {
  const { authenticatedUser } = useAuth();
  const { id } = useParams(); // Obtiene el id de la URL
  const location = useLocation();
  const [dropzone, setDropzone] = useState(false);
  const [cantidadAlimentos, setCantidadAlimentos] = useState({});
  const [showCommands, setShowCommands] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedGalpon, setSelectedGalpon] = useState(null);
  const [aves, setAves] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [controlAves, setControlAves] = useState([]);
  const [controlAlimentos, setControlAlimentos] = useState([]);
  const [alimentos, setAlimentos] = useState([]);
  const [AllGranjas, setAllGranjas] = useState([]);
  const [granjas, setGranjas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [galpones, setGalpones] = useState([]);
  const [granja, setGranja] = useState([]);
  const [showAddGalpon, setShowAddGalpon] = useState(false);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [showAddFrequentTask, setShowAddFrequentTask] = useState(false);
  const [showAddAves, setShowAddAves] = useState(false);
  const [showAddEgg, setShowAddEgg] = useState(false);
  const [showAddAlimento, setShowAddAlimento] = useState(false);
  const [showAddGranja, setShowAddGranja] = useState(false);
  const [showAddControlAves, setShowAddControlAves] = useState(false);
  const [showAddControlAlimentos, setShowAddControlAlimentos] = useState(false);
  const [showAddProduccion, setShowAddProduccion] = useState(false);
  const [showDespachos, setShowDespachos] = useState(false);
  const [showAddCompleteRegister, setShowAddCompleteRegister] = useState(false);
  const [produccionHuevos, setProduccionHuevos] = useState([]);

  const [selectedAve, setSelectedAve] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingGranjas, setLoadingGranjas] = useState(true);
  const [granjaActiva, setGranjaActiva] = useState(
    () => localStorage.getItem("granjaActiva") || null
  );
  const [showSidebar, setShowSidebar] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState(null);
  const [showAves, setShowAves] = useState(true);
  const [showAlimentos, setShowAlimentos] = useState(false);
  const [showTables, setShowTables] = useState(false);

  // Condición para ocultar el botón AddFarm en la ruta /dashboard/granjas/:id
  const isGranjaPage = location.pathname.match(/^\/mis-granjas/);
  const isAlimentosPage = location.pathname.match(/^\/mis-alimentos/);

  const isAvesPage = location.pathname.match(/^\/mis-aves/);
  const isGalponesPage = location.pathname.match(/^\/mis-galpones/);
  const isConfiguracionPage = location.pathname.match(/^\/configuracion/);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const fetchGalpones = useCallback(() => {
    if (!granjaActiva) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/galpones`, {
        params: { granjaId: granjaActiva },
      })
      .then((response) => {
        setGalpones(response.data);
      })
      .catch((error) => {
        console.error("Error fetching galpones:", error);
        // Asumiendo que tienes una función `setError` para manejar errores
        setError(error.message || "Failed to fetch galpones.");
      });
  }, [granjaActiva, setGalpones]);

  // Función para verificar si hay galpones
  const hasGalpones = () => {
    return galpones.length > 0;
  };

  const hasAves = () => {
    return aves.length > 0;
  };

  // Definir fetchAves usando useCallback
  const fetchMisAves = useCallback(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/aves/mis-aves`, {
        params: { granjaId: granjaActiva },
      })
      .then((response) => {
        setAves(response.data);
      })
      .catch((error) => {
        console.error("Error fetching aves:", error);
        setError(getErrorMessage(error));
      });
  }, [granjaActiva]); // Solo se recreará si granjaActiva cambia

  const fetchControlAlimentos = useCallback(() => {
    if (!granjaActiva) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/control-alimentos`, {
        params: { granjaId: granjaActiva },
      })
      .then((response) => setControlAlimentos(response.data))
      .catch((error) => {
        console.error("Error fetching control de alimentos:", error);
        setError(getErrorMessage(error));
      });
  }, [granjaActiva, setControlAlimentos, setError]);

  const fetchProduccionHuevos = useCallback(() => {
    if (!granjaActiva) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/produccion-huevos`, {
        params: { granjaId: granjaActiva },
      })
      .then((response) => {
        setProduccionHuevos(response.data);
      })
      .catch((error) => {
        console.error(
          "Error fetching producción de huevos:",
          error.response?.data || error.message
        );
      });
  }, [granjaActiva, setProduccionHuevos]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users`
      );
      console.log(response.usuarios);
      setUsuarios(response.data);
    } catch (error) {
      console.error(
        "Error fetching producción de huevos:",
        error.response?.data || error.message
      );
      console.log("Error details:", error); // Agrega este log para detalles completos del error
      setError(getErrorMessage(error));
    }
  }, []); // Solo se recreará si granjaActiva cambia

  const fetchGranjas = () => {
    if (!authenticatedUser) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/granjas`, {
        params: { userId: authenticatedUser.id },
      })
      .then((response) => {
        console.log("Granjas response:", response.data);
        setAllGranjas(response.data);
        setLoadingGranjas(false);
      })
      .catch((error) => {
        console.error("Error fetching granjas:", error);
        setError(getErrorMessage(error));
        setLoadingGranjas(false);
      });
  };

  const fetchGranjaById = (id) => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/granjas/${id}`)
      .then((response) => {
        console.log("Granja response:", response.data);
        setGranja(response.data);
      })
      .catch((error) => {
        console.error("Error fetching granja:", error);
        setError(getErrorMessage(error));
      });
  };

  const fetchAlimentos = useCallback(() => {
    const token = localStorage.getItem("authToken");

    axios
      .get(`${process.env.REACT_APP_API_URL}/alimentos/mis-alimentos`, {
        headers: { Authorization: `Bearer ${token}` }, // Asegúrate de pasar el token de autenticación si es necesario
      })
      .then((response) => {
        if (response.status === 200) {
          setAlimentos(response.data); // Actualiza el estado con los alimentos del usuario autenticado
        } else {
          console.error(`Unexpected status code: ${response.status}`);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          console.warn("No se encontraron alimentos para este usuario.");
          setAlimentos([]); // Establece una lista vacía si no se encuentran alimentos
        } else {
          console.error("Error fetching mis alimentos:", error);
          setError(getErrorMessage(error));
        }
      });
  }, [setAlimentos, setError]);

  const fetchMisGalpones = useCallback(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/galpones/mis-galpones`)
      .then((response) => {
        setGalpones(response.data);
      })
      .catch((error) => {
        console.error("Error fetching mis galpones:", error);
        setError(getErrorMessage(error));
      });
  }, []); // Only re-fetch when necessary

  const fetchMisGranjas = useCallback(() => {
    const token = localStorage.getItem("authToken");
    axios
      .get(`${process.env.REACT_APP_API_URL}/granjas/mis-granjas`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .then((response) => {
        setGranjas(response.data);
        setLoadingGranjas(false);
      })
      .catch((error) => {
        console.error("Error fetching mis granjas:", error);
        setError(getErrorMessage(error));
        setLoadingGranjas(false);
      });
  }, []);

  const fetchControlAves = useCallback(() => {
    if (!granjaActiva) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/control-aves`, {
        params: { granjaId: granjaActiva },
      })
      .then((response) => setControlAves(response.data))
      .catch((error) => {
        console.error("Error fetching control de aves:", error);
        setError(getErrorMessage(error));
      });
  }, [granjaActiva, setControlAves, setError]); // Dependencias

  const getAveNombre = (aveId) => {
    const ave = aves.find((ave) => ave.id === aveId);
    return ave ? ave.nombre : "No encontrado";
  };

  const getAlimentoNombre = (alimentoId) => {
    const alimento = alimentos.find((alimento) => alimento.id === alimentoId);
    return alimento ? alimento.nombre : "No encontrado";
  };
  const getGalponNombre = (galponId) => {
    const galpon = galpones.find((galpon) => galpon.id === galponId);
    return galpon ? galpon.nombre : "No encontrado";
  };

  const getGranjaNombre = (granjaId) => {
    console.log("Estado actual de loadingGranjas:", loadingGranjas);
    console.log("Lista de granjas:", granjas);
    console.log("granjaId que se está buscando:", granjaId);

    if (loadingGranjas || !granjas || granjas.length === 0) {
      console.log("Cargando granjas o la lista está vacía.");
      return "Cargando...";
    }

    const granja = granjas.find((granja) => granja.id === granjaId);

    if (granja) {
      console.log("Granja encontrada:", granja);
      return granja.nombre;
    } else {
      console.log("Granja no encontrada con granjaId:", granjaId);
      return "No encontrado";
    }
  };

  const formatDate = (date) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "eee, dd 'de' MMMM 'del' yyyy", { locale: es });
  };

  const calcularConsumoPorAve = (ingestaDiariaKg, saldo) => {
    if (saldo > 0) {
      const ingestaDiariaGramos = ingestaDiariaKg * 1000;
      return (ingestaDiariaGramos / saldo).toFixed(2);
    }
    return 0;
  };

  const calcularPorcentajePostura = (idAve) => {
    // Filtrar los registros de producción para el ave específica
    const registrosProduccion = produccionHuevos.filter(
      (produccion) => produccion.id_ave === idAve
    );

    // Obtener el último registro de producción
    const ultimoRegistro = registrosProduccion[registrosProduccion.length - 1];

    if (!ultimoRegistro) {
      // Si no hay registros de producción, retorna 0
      return 0;
    }

    // Obtener el total de huevos comerciales del último registro
    const totalHuevos = ultimoRegistro.comerciales;

    // Obtener el saldo actual de aves
    const ave = aves.find((ave) => ave.id === idAve);
    const saldoActual = ave ? ave.saldo : 0;

    // Calcular el porcentaje de postura
    if (saldoActual > 0) {
      const porcentajePostura = (totalHuevos / saldoActual) * 100;
      return porcentajePostura.toFixed(2);
    }

    return 0;
  };

  const calcularPorcentajePosturaComercial = (idAve) => {
    const registrosProduccion = produccionHuevos.filter(
      (produccion) => produccion.id_ave === idAve
    );

    const totalHuevosComerciales = registrosProduccion.reduce(
      (total, registro) => total + registro.comerciales,
      0
    );

    const ave = aves.find((ave) => ave.id === idAve);
    const saldoActual = ave ? ave.saldo : 0;

    if (saldoActual > 0) {
      const porcentajePosturaComercial =
        (totalHuevosComerciales / saldoActual) * 100;
      return porcentajePosturaComercial.toFixed(2);
    }
    return 0;
  };

  const handleOpenTask = (url) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      console.error("No URL provided");
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today; // Formatear la fecha correctamente
  };

  useEffect(() => {
    if (id) {
      setGranjaActiva(id);
    }
  }, [id]);

  const [selectedProduccion, setSelectedProduccion] = useState(null);
  const [todayDate, setTodayDate] = useState("");

  const formatDateToISO = (date) => {
    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0); // Asegura que la hora no afecte la comparación
    return d.toISOString().split("T")[0]; // Devuelve solo la parte de la fecha
  };

  const handleDeleteProduccionHuevos = async (id) => {
    if (
      window.confirm(
        "¿Estás seguro de que deseas eliminar este registro de producción de huevos?"
      )
    ) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/produccion-huevos/${id}`
        );
        setProduccionHuevos(
          produccionHuevos.filter((produccion) => produccion.id !== id)
        );
        fetchProduccionHuevos(); // Optionally refresh the data
        console.log("Registro de producción de huevos eliminado con éxito");
      } catch (error) {
        console.error(
          "Error al eliminar el registro de producción de huevos:",
          error
        );
        alert(
          "Ocurrió un error al eliminar el registro. Por favor, intenta nuevamente."
        );
      }
    }
  };

  const handleDeleteGalpon = async (galponId) => {
    setLoading(true); // Inicia la carga
    console.log(`Iniciando eliminación del galpón con ID: ${galponId}`);

    try {
      // Obtener todas las aves asociadas al galpón
      const avesDelGalpon = aves.filter((ave) => ave.galponId === galponId);
      console.log(
        `Aves asociadas al galpón con ID ${galponId}:`,
        avesDelGalpon
      );

      // Si no hay aves en el galpón, avisa y continúa eliminando el galpón
      if (avesDelGalpon.length === 0) {
        console.log(
          `No hay aves en el galpón con ID ${galponId}, procediendo a eliminar el galpón.`
        );
      }

      // Para cada ave, eliminar los registros relacionados
      for (const ave of avesDelGalpon) {
        console.log(`Procesando la eliminación de la ave con ID: ${ave.id}`);

        // 1. Eliminar producción de huevos para este ave
        try {
          console.log(
            `Eliminando producción de huevos para la ave con ID: ${ave.id}`
          );
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/produccion-huevos/ave/${ave.id}`
          );
        } catch (error) {
          console.warn(
            `No se encontró producción de huevos para la ave con ID: ${ave.id}, omitiendo.`
          );
        }

        // 2. Eliminar control de alimentos para este ave
        try {
          console.log(
            `Eliminando control de alimentos para la ave con ID: ${ave.id}`
          );
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/control-alimentos/ave/${ave.id}`
          );
        } catch (error) {
          console.warn(
            `No se encontró control de alimentos para la ave con ID: ${ave.id}, omitiendo.`
          );
        }

        // 3. Eliminar control de aves para este ave
        try {
          console.log(
            `Eliminando control de aves para la ave con ID: ${ave.id}`
          );
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/control-aves/ave/${ave.id}`
          );
        } catch (error) {
          console.warn(
            `No se encontró control de aves para la ave con ID: ${ave.id}, omitiendo.`
          );
        }

        // 4. Finalmente, eliminar el ave
        try {
          console.log(`Eliminando la ave con ID: ${ave.id}`);
          await axios.delete(`${process.env.REACT_APP_API_URL}/aves/${ave.id}`);
        } catch (error) {
          console.error(`Error eliminando la ave con ID: ${ave.id}:`, error);
        }
      }

      // Después de eliminar todas las aves, eliminar el galpón
      console.log(`Eliminando el galpón con ID: ${galponId}`);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/galpones/${galponId}`
      );

      // Actualizar el estado de aves, producción de huevos, y control de alimentos
      console.log(
        "Actualizando el estado después de eliminar galpón y sus aves..."
      );
      await Promise.all([
        fetchMisAves(),
        fetchProduccionHuevos(),
        fetchControlAlimentos(),
        fetchControlAves(),
      ]);

      console.log("Galpón y sus aves eliminados correctamente");
    } catch (error) {
      console.error("Error al eliminar el galpón o las aves:", error);
      setError(error.message || "Error eliminando el galpón");
    } finally {
      setLoading(false); // Finaliza la carga
      console.log("Finalizada la operación de eliminación del galpón.");
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date()); // Estado compartido para la fecha
  const [selectedFilterDate, setSelectedFilterDate] = useState(null); // Estado compartido para la fecha

  const [selectedLote, setSelectedLote] = useState(null);
  const [showIconTicker, setShowIconTicker] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const isToday = (date) => {
    if (!date) return false; // Si `date` es null o undefined, devuelve false
    const today = new Date();
    return today.toDateString() === new Date(date).toDateString();
  };

  const renderDateTitle = (date) => {
    // Crear un objeto Date a partir de la fecha recibida
    const parsedDate = new Date(date);

    // Ajustar el día agregando la diferencia horaria
    parsedDate.setDate(parsedDate.getDate() + 1);

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return parsedDate.toLocaleDateString("es-ES", options);
  };

  const fetchGalponAves = useCallback(
    async (galponId) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/aves/galpon/${galponId}`
        );
        setAves(response.data); // Actualiza el estado con las aves específicas del galpón
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setAves([]); // Si no hay aves, establecer el estado como una lista vacía
          console.log("No hay aves para este galpón");
        } else {
          console.error("Error fetching galpón aves:", error);
        }
      }
    },
    [setAves]
  );

  const clearUserData = useCallback(() => {
    setAlimentos([]); // Limpiar los alimentos
    setGranjas([]); // Limpiar las granjas
    setGalpones([]); // Limpiar los galpones
  }, []);

  const [isSignInActive, setIsSignInActive] = useState(true);
  const [animationClass, setAnimationClass] = useState("");
  const [isButtonClicked, setIsButtonClicked] = useState(true);
  const [selectedAlimento, setSelectedAlimento] = useState(null);
  const [query, setQuery] = useState(""); // Almacena el valor de búsqueda

  // Agrega en el ShoppingContext
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Función para alternar entre light mode y dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const [showFarmer, setShowFarmer] = useState(false);
  const [farmerMessage, setFarmerMessage] = useState("");
  const [actionButtonText, setActionButtonText] = useState("");
  const [handleAction, setHandleAction] = useState(() => {});

  const [activeButton, setActiveButton] = useState("");

  // Dentro de ShoppingContext

  // Calcular el total de alimento consumido (en kg)
  const calcularTotalAlimentoConsumido = () => {
    return controlAlimentos.reduce((total, registro) => {
      return total + (registro.cantidad_consumida || 0); // Sumar la cantidad consumida de cada registro
    }, 0);
  };

  // Calcular el alimento disponible en stock (en kg)
  const calcularAlimentoDisponible = () => {
    return alimentos.reduce((total, alimento) => {
      return total + (alimento.cantidad_disponible || 0); // Sumar la cantidad disponible de cada alimento
    }, 0);
  };

  // Agregar los valores calculados al contexto
  const totalAlimentoConsumido = calcularTotalAlimentoConsumido();
  const alimentoDisponible = calcularAlimentoDisponible();

  const [galponCreatedFromAveForm, setGalponCreatedFromAveForm] =
    useState(false); // Nuevo estado

  const [isDataFetched, setIsDataFetched] = useState(false);

  const [tiposHuevos, setTiposHuevos] = useState([]);

  const fetchTiposHuevos = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/tipos-huevos`);
      console.log("Tipos de huevo recibidos:", response.data); // Agrega este log
  
      if (response.data && Array.isArray(response.data)) {
        setTiposHuevos(response.data);
      }
    } catch (error) {
      console.error("Error al obtener los tipos de huevo", error);
    }
  }, [setTiposHuevos]);
  
  const [tiposHuevosProduccion, setTiposHuevosProduccion] = useState([]);
  
  // Función para obtener los tipos de huevos de una producción
  const getTipoHuevoByProduccionId = async (produccionId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/produccion-huevos/${produccionId}/tipos-huevos`
      );
      setTiposHuevosProduccion(response.data);
    } catch (error) {
      console.error('Error al obtener los tipos de huevo por producción:', error);
    }
  };

  
  const [nuevoGalpon, setNuevoGalpon] = useState(null);
  const [isSwipingAves, setIsSwipingAves] = useState(false); // Controla si se está deslizando

  return (
    <ShoppingContext.Provider
      value={{
        nuevoGalpon, setNuevoGalpon,isSwipingAves, setIsSwipingAves,
        tiposHuevosProduccion, setTiposHuevosProduccion,
        getTipoHuevoByProduccionId,
        tiposHuevos,
        setTiposHuevos,
        fetchTiposHuevos,
        isDataFetched,
        setIsDataFetched,
        handleDeleteGalpon,
        galponCreatedFromAveForm,
        setGalponCreatedFromAveForm,
        totalAlimentoConsumido,
        alimentoDisponible,
        activeButton,
        setActiveButton,
        selectedFilterDate,
        setSelectedFilterDate,
        showFarmer,
        setShowFarmer,
        handleAction,
        setHandleAction,
        actionButtonText,
        setActionButtonText,
        farmerMessage,
        setFarmerMessage,
        toggleDarkMode,
        isDarkMode,
        setIsDarkMode,
        query,
        setQuery,
        selectedAlimento,
        setSelectedAlimento,

        isAlimentosPage,
        animationClass,
        setAnimationClass,
        isButtonClicked,
        setIsButtonClicked,
        isSignInActive,
        setIsSignInActive,
        showAddEgg,
        setShowAddEgg,
        clearUserData,
        showIconTicker,
        setShowIconTicker,
        hasGalpones,
        hasAves,
        usuarios,
        setUsuarios,
        fetchGranjaById,
        fetchAlimentos,
        fetchControlAves,
        fetchControlAlimentos,
        fetchProduccionHuevos,
        fetchGranjas,
        fetchUsuarios,
        fetchGalpones,
        fetchMisGranjas,
        fetchMisGalpones,
        fetchMisAves,
        currentPage,
        setCurrentPage,
        selectedProduccion,
        setSelectedProduccion,
        formatDateToISO,
        getGranjaNombre,
        showSidebar,
        setShowSidebar,
        showTables,
        setShowTables,
        showCommands,
        setShowCommands,
        calcularPorcentajePostura,
        calcularPorcentajePosturaComercial,
        controlAlimentos,
        setControlAlimentos,
        handleOpenTask,
        galpones,
        showAddAlimento,
        setShowAddAlimento,
        showAddAves,
        setShowAddAves,
        showAddFrequentTask,
        setShowAddFrequentTask,
        getAveNombre,
        getAlimentoNombre,
        formatDate,
        isGranjaPage,
        isConfiguracionPage,
        getGalponNombre,
        selectedGalpon,
        setSelectedGalpon,
        isAvesPage,
        isGalponesPage,
        calcularConsumoPorAve,
        produccionHuevos,
        setProduccionHuevos,
        showAves,
        setShowAves,
        showAddControlAves,
        setShowAddControlAves,
        showAddControlAlimentos,
        setShowAddControlAlimentos,
        controlAves,
        setControlAves,
        showAddProduccion,
        setShowAddProduccion,
        showAddBatch,
        setShowAddBatch,
        showAlimentos,
        setShowAlimentos,
        showDespachos,
        setShowDespachos,
        showAddGalpon,
        setShowAddGalpon,
        showAddCompleteRegister,
        setShowAddCompleteRegister,
        aves,
        setAves,
        alimentos,
        setAlimentos,
        setAllGranjas,
        AllGranjas,
        isSidebarOpen,
        setIsSidebarOpen,
        setGalpones,
        toggleSidebar,
        showLoginForm,
        setShowLoginForm,
        showSignUpForm,
        setShowSignUpForm,
        error,
        setError,
        granjas,
        setGranjas,
        granjaActiva,
        setGranjaActiva,
        showAddGranja,
        setShowAddGranja,
        granja,
        setGranja,
        lotes,
        setLotes,
        id,
        getTodayDate,
        loading,
        setLoading,
        cantidadAlimentos,
        setCantidadAlimentos,
        todayDate,
        setTodayDate,
        selectedLote,
        setSelectedLote,
        selectedDate,
        setSelectedDate,
        handleDeleteProduccionHuevos,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        dropzone,
        setDropzone,
        modalOpen,
        setModalOpen,
        renderDateTitle,
        isToday,
        selectedAve,
        setSelectedAve,
        fetchGalponAves,
      }}
    >
      {children}
    </ShoppingContext.Provider>
  );
};

export const useShoppingContext = () => useContext(ShoppingContext);
